/**
 * Safe date conversion utilities for handling various date formats
 * from Firestore and other sources
 */

/**
 * Safely converts various date formats to ISO date string (YYYY-MM-DD)
 * Handles Firestore timestamps, JavaScript Dates, and date strings
 *
 * @param {*} dateValue - The date value to convert (can be Firestore timestamp, Date object, string, or null)
 * @returns {string} - ISO date string (YYYY-MM-DD) or empty string if invalid
 */
export const safeConvertToISODate = (dateValue) => {
  try {
    // Handle null, undefined, or empty values
    if (!dateValue) {
      return "";
    }

    let jsDate;

    // Handle Firestore Timestamp objects (with .seconds property)
    if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
      jsDate = new Date(dateValue.seconds * 1000);
    }
    // Handle Firestore Timestamp objects (with .toDate method)
    else if (dateValue && typeof dateValue === 'object' && typeof dateValue.toDate === 'function') {
      jsDate = dateValue.toDate();
    }
    // Handle JavaScript Date objects
    else if (dateValue instanceof Date) {
      jsDate = dateValue;
    }
    // Handle date strings or numbers
    else {
      jsDate = new Date(dateValue);
    }

    // Check if the resulting date is valid
    if (isNaN(jsDate.getTime())) {
      console.warn("Invalid date value:", dateValue);
      return "";
    }

    // Return ISO date string (YYYY-MM-DD)
    return jsDate.toISOString().split("T")[0];

  } catch (error) {
    console.error("Error converting date:", dateValue, error);
    return "";
  }
};

/**
 * Safely converts various date formats to localized date string
 *
 * @param {*} dateValue - The date value to convert
 * @param {string} locale - The locale to use (default: 'en-CA')
 * @returns {string} - Localized date string or empty string if invalid
 */
export const safeConvertToLocaleDateString = (dateValue, locale = 'en-CA') => {
  try {
    if (!dateValue) {
      return "";
    }

    let jsDate;

    // Handle Firestore Timestamp objects (with .seconds property)
    if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
      jsDate = new Date(dateValue.seconds * 1000);
    }
    // Handle Firestore Timestamp objects (with .toDate method)
    else if (dateValue && typeof dateValue === 'object' && typeof dateValue.toDate === 'function') {
      jsDate = dateValue.toDate();
    }
    // Handle JavaScript Date objects
    else if (dateValue instanceof Date) {
      jsDate = dateValue;
    }
    // Handle date strings or numbers
    else {
      jsDate = new Date(dateValue);
    }

    // Check if the resulting date is valid
    if (isNaN(jsDate.getTime())) {
      console.warn("Invalid date value:", dateValue);
      return "";
    }

    // Return localized date string
    return jsDate.toLocaleDateString(locale);

  } catch (error) {
    console.error("Error converting date:", dateValue, error);
    return "";
  }
};

/**
 * Safely checks if a date value exists and is valid
 *
 * @param {*} dateValue - The date value to check
 * @returns {boolean} - True if valid date exists, false otherwise
 */
export const isValidDate = (dateValue) => {
  try {
    if (!dateValue) {
      return false;
    }

    let jsDate;

    if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
      jsDate = new Date(dateValue.seconds * 1000);
    } else if (dateValue && typeof dateValue === 'object' && typeof dateValue.toDate === 'function') {
      jsDate = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      jsDate = dateValue;
    } else {
      jsDate = new Date(dateValue);
    }

    return !isNaN(jsDate.getTime());

  } catch (error) {
    return false;
  }
};